import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Popover } from 'react-tiny-popover'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'

import ListFilterTab from './ListFilterTab'
import PopoverFilterButton from './PopoverFilterButton'
import PopoverWrapper from './PopoverWrapper'

import { isNorthAmericanRegion } from '../../../configuration/brand'
import { filterKeys, filters } from '../../../configuration/filtering'
import { useGetFilterContentFromResults } from '../../../hooks/useGetFilterContentFromResults'
import { useGetFilteredToursWithException } from '../../../hooks/useGetFilteredToursWithException'
import { updateExactMatchFilter, updateFilter } from '../../../redux/actions/applyFilters'
import { getBrand } from '../../../redux/reducers/configuration'
import { getFilterSetByKey, getIsFilterExactMatch } from '../../../redux/reducers/filters'
import { filterToursByField } from '../../../utilities/filters/filterToursByField'

import '../../../styles/scss/filters/popover-list-filter.scss'

function getVacationTypesLabel(brandId) {
  return isNorthAmericanRegion(brandId)
    ? 'Type of Vacation'
    : 'Type of Holiday'
}

const PopoverListFilter = ({ filterKey, saveFiltersByKey }) => {
  const dispatch = useDispatch()
  const activeFilters = useSelector(state => getFilterSetByKey(state, filterKey))
  const brandId = useSelector(getBrand)
  const exactMatchFilterEnabled = useSelector(state => getIsFilterExactMatch(state, filterKey))
  const [currentExactMatch, setCurrentExactMatch] = useState(exactMatchFilterEnabled)
  const [currentFilters, setCurrentFilters] = useState(activeFilters)
  const [popoverOpen, setPopoverOpen] = useState(false)

  const tours = useGetFilteredToursWithException(filterKey)
  const totalResults = useMemo(() => {
    return _.size(filterToursByField(tours, filterKey, currentFilters, currentExactMatch))
  }, [currentExactMatch, currentFilters, filterKey, tours])

  const filtersToDisplayInPopover = useGetFilterContentFromResults(filterKey)

  useEffect(() => {
    setCurrentExactMatch(exactMatchFilterEnabled)
    setCurrentFilters(activeFilters)
  }, [activeFilters, exactMatchFilterEnabled, popoverOpen])

  function onClickFilterTab(filter, isActive) {
    setCurrentFilters(filterValues => {
      const updatedFilters = isActive
        ? _.without(currentFilters, filter)
        : _.concat(currentFilters, filter)
      if (_.isEmpty(updatedFilters)) {
        setCurrentExactMatch(false)
      }
      return updatedFilters
    })
  }

  function onClearAllFilters() {
    dispatch(updateExactMatchFilter(filterKey, _.without))
    dispatch(updateFilter(filterKey, _.get(filters, `[${filterKey}].initialState`)))
    setPopoverOpen(false)
  }

  function onOpenPopover() {
    setPopoverOpen(true)
  }

  const onSubmitFilters = useCallback(() => {
    setPopoverOpen(false)
    if (exactMatchFilterEnabled !== currentExactMatch) {
      dispatch(updateExactMatchFilter(filterKey, currentExactMatch ? _.concat : _.without))
    }
    dispatch(updateFilter(filterKey, currentFilters))
  }, [currentExactMatch, currentFilters, dispatch, exactMatchFilterEnabled, filterKey, ])

  const filterLabel = filterKey === filterKeys.vacationTypes
    ? getVacationTypesLabel(brandId)
    : _.get(filters, `[${filterKey}].label`)
  const filterCount = _.size(activeFilters) || _.size(filtersToDisplayInPopover)
  const totalResultsLabel = `Show ${totalResults} Result${totalResults === 1 ? '' : 's'}`

  return !_.isEmpty (filtersToDisplayInPopover) && (
    <div className="popover-list-filter">
      <Popover
        containerClassName="popover-list-modal"
        containerParent={document.getElementById('ylg-ms__site-search-listings')}
        containerStyle={{boxShadow: '2px 1px 4px rgba(0, 0, 0, 0.25)'}}
        isOpen={popoverOpen}
        onClickOutside={() => setPopoverOpen(false)}
        positions={['top', 'bottom', 'left', 'right']}
        content={(
          <PopoverWrapper
            id={filterKey}
            exactMatch={currentExactMatch}
            exactMatchLabel={_.get(filters, `[${filterKey}].exactMatchLabel`)}
            hasExactMatchOption={
              !_.isEmpty(currentFilters)
              && Boolean(_.get(filters, `[${filterKey}].hasExactMatchOption`))
            }
            onClear={onClearAllFilters}
            onClose={() => setPopoverOpen(false)}
            onSubmit={onSubmitFilters}
            onToggleExactMatch={() => setCurrentExactMatch(value => !value)}
            submitLabel={totalResultsLabel}
            title={filterLabel}
          >
            <div className="popover-list-modal__body">
              {_.map(filtersToDisplayInPopover, (filter, key) => (
                <ListFilterTab
                  key={filter}
                  isActive={saveFiltersByKey
                    ? _.includes(currentFilters, key)
                    : _.includes(currentFilters, filter)}
                  label={filter}
                  onClick={onClickFilterTab}
                  value={saveFiltersByKey ? key : filter}
                />
              ))}
            </div>
          </PopoverWrapper>
        )}
      >
        <PopoverFilterButton
          innerId={`${filterKey}-popover-tab`}
          isActive={Boolean(_.size(activeFilters))}
          label={`${filterLabel} (${filterCount})`}
          onClick={onOpenPopover}
        />
      </Popover>
    </div>
  )
}

PopoverListFilter.propTypes = {
  filterKey: PropTypes.string.isRequired,
}

PopoverListFilter.defaultProps = {
  displayAutoCompleteInput: false,
}

export default PopoverListFilter
